import { ThemeProvider } from '@emotion/react';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useLocation } from 'react-router';

import { TilingEngine } from '~/ui/domain/tiling-engine/TilingEngine';
import { theme } from '~/ui/theme/cobrainer-theme';

import { TileAccessGuard } from './availability/TileAccessGuard';
import type { UrlTileChromeRenderer, UrlTileRenderContext } from './types';
import { useUrlTiles } from './use-url-tiles';

export const UrlTileRenderer: FunctionComponent<{ readonly renderTileChrome?: UrlTileChromeRenderer }> = ({
    renderTileChrome,
}) => {
    const location = useLocation();
    const { closeTile, openTile, tiles, updateTile } = useUrlTiles();
    const renderContext: UrlTileRenderContext = {
        closeTile,
        location,
        openTile,
        updateTile,
    };

    return (
        <>
            {tiles.map((tile) => {
                const content = tile.definition.renderPayload(tile.payload, renderContext);

                if (is.undefined(content)) {
                    return null;
                }

                const guardedContent = (
                    <TileAccessGuard context={renderContext} tile={tile}>
                        {content}
                    </TileAccessGuard>
                );
                const tileContent =
                    renderTileChrome?.({ content: guardedContent, context: renderContext, tile }) ?? guardedContent;

                return (
                    <TilingEngine.Portal
                        key={tile.slotKey}
                        id={tile.slotKey}
                        maxWidth={tile.definition.maxWidth}
                        minWidth={tile.definition.minWidth}
                        width={tile.definition.width}
                    >
                        <ThemeProvider theme={theme}>{tileContent}</ThemeProvider>
                    </TilingEngine.Portal>
                );
            })}
        </>
    );
};
